import { type Table } from '@tanstack/react-table';

import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

type HandleColumnReorderArgs<T extends object> = {
	source: any;
	target: any;
	charlieTableState: Table<T>;
	columnOrder: string[];
};

/**
 * Handles column reordering logic for both pinned and unpinned columns.
 */
export const handleColumnReorder = <T extends object>({
	source,
	target,
	charlieTableState,
	columnOrder,
}: HandleColumnReorderArgs<T>): void => {
	const pinning = charlieTableState.getState().columnPinning;
	const sourceColumnId = source.data.id as string;
	const targetColumnId = target.data.id as string;
	const isSourceLeftPinned = pinning?.left?.includes(sourceColumnId);
	const isTargetLeftPinned = pinning?.left?.includes(targetColumnId);

	// Handle left-pinned column reordering
	if (isSourceLeftPinned && isTargetLeftPinned && pinning?.left) {
		const leftPinned = [...pinning.left];
		const sourceIndex = leftPinned.indexOf(sourceColumnId);
		const targetIndex = leftPinned.indexOf(targetColumnId);

		if (sourceIndex < 0 || targetIndex < 0) {
			return;
		}

		const closestEdgeOfTarget = extractClosestEdge(target.data);
		let finishIndex = targetIndex;

		if (closestEdgeOfTarget === 'right') {
			finishIndex = targetIndex + 1;
		}

		if (sourceIndex < finishIndex) {
			finishIndex--;
		}

		if (finishIndex === sourceIndex) {
			return;
		}

		const [removed] = leftPinned.splice(sourceIndex, 1);
		leftPinned.splice(finishIndex, 0, removed);

		charlieTableState.setColumnPinning({
			...pinning,
			left: leftPinned,
		});

		// Update columnOrder to ensure body is reordered, not just header
		const currentOrder = [...columnOrder];
		const orderSourceIndex = currentOrder.indexOf(sourceColumnId);
		const orderTargetIndex = currentOrder.indexOf(targetColumnId);

		if (orderSourceIndex >= 0 && orderTargetIndex >= 0) {
			currentOrder.splice(orderSourceIndex, 1);
			const newTargetIndex = currentOrder.indexOf(targetColumnId);
			const insertIndex = closestEdgeOfTarget === 'right' ? newTargetIndex + 1 : newTargetIndex;
			currentOrder.splice(insertIndex, 0, sourceColumnId);
			charlieTableState.setColumnOrderWithContext(currentOrder, { trigger: 'drag' });
		}
		return;
	}

	// Handle unpinned column reordering
	const columnIds = columnOrder;
	const startIndex = columnIds.findIndex((item) => item === source.data.id);
	if (startIndex < 0) {
		return;
	}
	const indexOfTarget = columnIds.findIndex((item) => item === target.data.id);
	if (indexOfTarget < 0) {
		return;
	}
	const closestEdgeOfTarget = extractClosestEdge(target.data);
	const finishIndex = getReorderDestinationIndex({
		startIndex,
		closestEdgeOfTarget,
		indexOfTarget,
		axis: 'horizontal',
	});
	if (finishIndex === startIndex) {
		return;
	}

	const newOrder = reorder({
		list: columnIds,
		startIndex,
		finishIndex,
	});
	charlieTableState.setColumnOrderWithContext(newOrder, { trigger: 'drag' });
};
